import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { endOfDay } from 'date-fns';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import { useChannels } from '@/hooks/useChannels';
import { PlatformSelect } from '@/components/ui/PlatformSelect';
import { ChannelSelect, ChannelSelectOption } from '@/components/ui/ChannelSelect';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { isSocialPublishTarget, NON_SOCIAL_CHANNEL_PLATFORMS } from '@/constants/platforms';
import { getChannelAvatar } from '@/utils/channel-avatar';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useEscapeLayer } from '@/hooks/useEscapeLayer';
import { useDrawerFocus } from '@/hooks/useDrawerFocus';
import { filterTriggerHover } from '@/components/ui/filterTriggerStyles';

/**
 * AnalyticsFilterDrawer Component
 *
 * Slide-out drawer from the right with filter controls:
 * - Time range filter (7 Days, 30 Days, Custom)
 * - Platform filter
 * - Channel filter (filtered by selected platform)
 * - Date range picker (only when Custom time range is selected)
 * - Clear filters button
 *
 * Features:
 * - Slides in from right
 * - Dark backdrop overlay
 * - Closes on ESC key
 * - Closes on backdrop click
 * - Focus trap when open
 * - Body scroll lock when open
 */

interface AnalyticsFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AnalyticsFilterDrawer = ({ isOpen, onClose }: AnalyticsFilterDrawerProps) => {
  const { t } = useTranslation('analytics');
  const store = useAnalyticsStore();
  const { data: channels } = useChannels();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Filter channels by selected platform, exclude non-social asset channels
  // (CANVA/GDRIVE/WORDPRESS) and the separately-discontinued TWITTER.
  const filteredChannels = channels?.filter(ch => {
    if (!isSocialPublishTarget(ch.platform) || ch.platform === 'TWITTER') return false;
    if (!store.platform) return true;
    return ch.platform === store.platform;
  }) || [];

  // Channel options for ChannelSelect
  const channelSelectOptions: ChannelSelectOption[] = filteredChannels.map(ch => ({
    id: ch.id,
    name: ch.platformUsername || (ch.metadata?.displayName as string) || `Channel ${ch.id}`,
    platform: ch.platform,
    avatarUrl: getChannelAvatar(ch.metadata as Record<string, unknown> | undefined),
  }));

  // Convert ISO strings to Date for DateRangePicker
  const dateRangeValue = {
    from: store.customFrom ? new Date(store.customFrom) : null,
    to: store.customTo ? new Date(store.customTo) : null,
  };

  // Handle date range change from picker
  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => {
    store.setCustomDateRange(
      range.from ? range.from.toISOString() : store.customFrom || '',
      range.to ? endOfDay(range.to).toISOString() : store.customTo || ''
    );
  };

  // Clear filters handler
  const handleClearFilters = () => {
    store.setPlatform(null);
    store.setChannelId(null);
    store.setTimeRange('30d');
  };

  // Check if there are active filters (platform, channel, or non-default time range)
  const hasActiveFilters = store.platform !== null || store.channelId !== null || store.timeRange !== '30d';

  // Pill button style helper
  const pillClass = (active: boolean) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      active
        ? 'bg-gray-900 text-white border border-gray-900'
        : `border border-gray-200 text-gray-600 bg-white ${filterTriggerHover}`
    }`;

  /**
   * ESC closes this drawer only when it is the topmost layer and no inner popover consumed the key.
   */
  useEscapeLayer(isOpen, onClose);

  /**
   * Prevent body scroll when drawer is open
   */
  useBodyScrollLock(isOpen);

  // Inert + aria-hidden while closed; focus in on open, restore to the opener on close
  useDrawerFocus(isOpen, drawerRef);

  return (
    <div
      className={`fixed inset-0 z-40 transition-opacity duration-300 ease-in-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed right-0 top-navbar-height bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:bottom-0 w-full sm:max-w-md bg-white shadow-2xl z-50 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby="analytics-filter-drawer-title"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={ICONS.FILTER} size="sm" color="text-gray-600" />
            <h2 id="analytics-filter-drawer-title" className="text-sm lg:text-lg font-bold text-gray-900">
              {t('filters', { defaultValue: 'Filters' })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('close_filter_drawer', { defaultValue: 'Close filters' })}
          >
            <Icon name={ICONS.CROSS} size="sm" color="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('time_range', { defaultValue: 'Time Range' })}
            </label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => store.setTimeRange('7d')} className={pillClass(store.timeRange === '7d')}>
                {t('filter_7_days', { defaultValue: '7 Days' })}
              </button>
              <button type="button" onClick={() => store.setTimeRange('30d')} className={pillClass(store.timeRange === '30d')}>
                {t('filter_30_days', { defaultValue: '30 Days' })}
              </button>
              <button type="button" onClick={() => store.setTimeRange('custom')} className={pillClass(store.timeRange === 'custom')}>
                {t('filter_custom', { defaultValue: 'Custom' })}
              </button>
            </div>
          </div>

          {/* Date Range - Only show when Custom is selected */}
          {store.timeRange === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                {t('date_range', { defaultValue: 'Date Range' })}
              </label>
              <DateRangePicker
                size="sm"
                value={dateRangeValue}
                onChange={handleDateRangeChange}
                placeholder={t('select_date_range', { defaultValue: 'Select period' })}
                className="w-full"
              />
            </div>
          )}

          {/* Platform Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('filter_platform', { defaultValue: 'Platform' })}
            </label>
            <PlatformSelect
              size="sm"
              value={store.platform || 'ALL'}
              onChange={(val) => store.setPlatform(val === 'ALL' ? null : val)}
              allOptionLabel={t('filter_all_platforms', { defaultValue: 'All Platforms' })}
              exclude={[...NON_SOCIAL_CHANNEL_PLATFORMS, 'TWITTER']}
              className="w-full"
            />
          </div>

          {/* Channel Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('filter_channel', { defaultValue: 'Channel' })}
            </label>
            <ChannelSelect
              size="sm"
              value={store.channelId}
              onChange={(id) => store.setChannelId(id)}
              options={channelSelectOptions}
              placeholder={t('filter_all_channels', { defaultValue: 'All Channels' })}
              className="w-full"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 pb-6 sm:pb-4 flex gap-1.5 sm:gap-3">
          <Button
            variant="primary"
            onClick={onClose}
            className="flex-1 w-full"
          >
            {t('apply_filters', { defaultValue: 'Apply' })}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="secondary"
              onClick={handleClearFilters}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Icon name={ICONS.CROSS} size="sm" />
              {t('clear_filters', { defaultValue: 'Clear' })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
